import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { photoUrl } from "@/lib/reviews";
import { SplitText, Reveal } from "@/components/fx/Motion";

export const useBeforeAfter = (handle) => useQuery({ queryKey: ["before-after", handle || "all"], queryFn: () => axios.get(`${API}/before-after`, { params: { handle } }).then((r) => r.data) });

const Compare = ({ pair, i }) => {
  const { t } = useI18n();
  const [pos, setPos] = useState(50);
  return (
    <motion.figure data-testid={`before-after-${pair.id}`} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.12 }}>
      <div className="relative aspect-[4/5] overflow-hidden select-none bg-[#0e0d0b]">
        <img src={photoUrl(pair.after)} alt={t("ba_after")} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
        <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={photoUrl(pair.before)} alt={t("ba_before")} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
        </div>
        <span className="absolute top-3 left-3 bg-[#0b0a08]/80 text-[10px] uppercase tracking-[0.12em] px-2.5 py-1">{t("ba_before")}</span>
        <span className="absolute top-3 right-3 bg-gold text-[#0b0a08] text-[10px] uppercase tracking-[0.12em] font-bold px-2.5 py-1">{t("ba_after")}</span>
        <div className="absolute inset-y-0 w-px bg-[#e3cb94] pointer-events-none" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-gold text-[#0b0a08] flex items-center justify-center shadow-xl"><MoveHorizontal size={18} /></span>
        </div>
        <input data-testid={`before-after-slider-${pair.id}`} type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
          aria-label={`${t("ba_before")} / ${t("ba_after")}`} className="absolute inset-0 h-full w-full opacity-0 cursor-ew-resize" />
      </div>
      {pair.caption && <figcaption className="text-sm text-white/60 mt-3">{pair.caption}</figcaption>}
    </motion.figure>
  );
};

export const BeforeAfter = ({ handle }) => {
  const { t } = useI18n();
  const { data = [] } = useBeforeAfter(handle);
  if (!data.length) return null;
  return (
    <section data-testid="before-after-section" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
      <p className="eyebrow mb-4">{t("ba_eyebrow")}</p>
      <h2 className="font-display text-3xl lg:text-5xl"><SplitText text={t("ba_title")} /></h2>
      <Reveal delay={0.2}><p className="text-white/60 mt-4 mb-10 max-w-lg">{t("ba_text")}</p></Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{data.map((p, i) => <Compare key={p.id} pair={p} i={i} />)}</div>
    </section>
  );
};
